import { createRouter, createWebHistory, type RouteLocationNormalized, type NavigationGuardNext } from 'vue-router'
import { FormService } from '@shared/services/formService'

// Create a loading indicator for route validation
let isValidatingRoute = false

/**
 * Navigation guard for validating form slugs
 * Checks if form exists and is active before allowing navigation
 */
async function validateFormSlug(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const formSlug = to.params.formSlug as string

  if (!formSlug) {
    next({ name: 'not-found' })
    return
  }

  // Prevent multiple simultaneous validations
  if (isValidatingRoute) {
    next(false)
    return
  }

  try {
    isValidatingRoute = true

    // Add a small delay to show loading state for very fast networks
    const [result] = await Promise.all([
      FormService.validateFormSlug(formSlug),
      new Promise(resolve => setTimeout(resolve, 100))
    ])

    if (result.exists && result.isActive) {
      // Form is valid, proceed to the route
      next()
    } else if (result.exists && !result.isActive) {
      // Form exists but is inactive
      next({
        name: 'not-found',
        params: { formSlug },
        query: { reason: 'inactive' }
      })
    } else {
      // Form doesn't exist
      next({
        name: 'not-found',
        params: { formSlug }
      })
    }
  } catch (error) {
    console.error('Form validation error:', error)

    // Network or server error, go to error page
    next({
      name: 'error',
      params: { formSlug },
      query: {
        error: error instanceof Error ? error.message : 'Failed to validate form'
      }
    })
  } finally {
    isValidatingRoute = false
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Root path: redirect /?slug=<value> to /<value> preserving other query params
    {
      path: '/',
      redirect: (to) => {
        const slug = to.query.slug as string
        if (slug) {
          const { slug: _slug, ...restQuery } = to.query
          return { path: `/${slug}`, query: restQuery }
        }
        return { name: 'not-found' }
      },
    },

    // Error pages (must come before catch-all dynamic route)
    {
      path: '/not-found/:formSlug?',
      name: 'not-found',
      component: () => import('../views/NotFoundView.vue'),
      props: true,
    },
    {
      path: '/error/:formSlug?',
      name: 'error',
      component: () => import('../views/ErrorView.vue'),
      props: true,
    },

    // Catch-all dynamic form route (MUST be last)
    {
      path: '/:formSlug',
      name: 'dynamic-form',
      component: () => import('../views/DynamicFormViewRefactored.vue'),
      beforeEnter: validateFormSlug,
      props: (route) => ({
        formSlug: route.params.formSlug as string,
        urlParams: route.query as Record<string, string>,
      }),
      meta: {
        requiresValidation: true,
        fullscreen: true,
      },
    },

    // Fallback 404 for any other unmatched routes
    {
      path: '/:pathMatch(.*)*',
      name: '404',
      redirect: { name: 'not-found' },
    },
  ],
})

// Global navigation guards
router.beforeEach((to, from, next) => {
  // Set page title - always use the Polish title for booking forms
  if (to.name === 'dynamic-form' && to.params.formSlug) {
    document.title = 'Zaplanuj spotkanie z Tutlo'
  } else {
    document.title = 'Tutlo Meeting Scheduler'
  }

  next()
})

// Handle navigation errors
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
